import React, { useMemo, useState } from 'react';
import { DatePicker as MaterialUiDatePicker, LocalizationProvider } from '@mui/lab';
import { TextField } from '../TextField';
import { ChevronRight } from '@mui/icons-material';
import { useDateFns } from '~/Hooks';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { PLAIN_GREY } from '~/Constants/Colors.constants';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { IKeyboardDatePickerPropsOptions } from '../IKeyboardDatePickerPropsOptions';

export interface IKeyboardDatePickerProps {
  value: string | null;
  icon?: React.ReactElement;
  onDateChange?: (name: string, value: string | null | undefined) => void;
  defaultValue?: string;
  minDate?: Date;
  error?: boolean;
  disablePast?: boolean;
  name?: string;
  inputFormat?: string;
  label?: string;
  disabled?: boolean;
  options?: IKeyboardDatePickerPropsOptions;
}

export const KeyboardDatePicker = ({
  icon,
  onDateChange,
  minDate,
  defaultValue,
  value,
  error,
  disablePast,
  name,
  inputFormat,
  label,
  disabled,
  options,
}: IKeyboardDatePickerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate, localeDateFormat, isBeforeDate } = useDateFns();

  const valueAsDate = useMemo(
    () => (value === null ? null : new Date(value || defaultValue || new Date())),
    [value, defaultValue],
  );

  const [open, setOpen] = useState(false);
  const [dateInputValue, setDateInputValue] = useState(
    valueAsDate ? formatDate(valueAsDate.toString(), 'P') : null,
  );

  const _onDateChange = (date: Date | null, value?: string | null | undefined) => {
    if (date) {
      setDateInputValue(value || '');
      // check if the date is valid
      if (date != ('Invalid Date' as any)) {
        onDateChange?.(name || '', date.toISOString());
      }
    }
  };

  const onBlur = () => {
    if (valueAsDate) {
      if (disablePast) {
        const today = new Date().toISOString();
        if (isBeforeDate(valueAsDate.toISOString(), today)) {
          setDateInputValue(formatDate(today, 'P'));
          onDateChange?.(name || '', today);
        }
      }
      if (minDate && isBeforeDate(valueAsDate.toISOString(), minDate.toISOString())) {
        setDateInputValue(formatDate(minDate.toISOString(), 'P'));
        onDateChange?.(name || '', minDate.toISOString());
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MaterialUiDatePicker
        data-testid="KeyboardDatePicker"
        inputFormat={inputFormat || localeDateFormat}
        onChange={_onDateChange}
        value={valueAsDate}
        minDate={minDate}
        cancelText={t('app.cancel')}
        okText={t('app.confirm')}
        disablePast={disablePast}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        renderInput={(textProps: any) => (
          <TextField
            {...textProps}
            icon={icon}
            value={dateInputValue}
            label={label}
            error={error}
            disabled={disabled}
            newDesign
            errorOutline={error}
            onBlur={onBlur}
            options={options}
          />
        )}
        desktopModeMediaQuery={theme.breakpoints.up('md')}
        disabled={disabled}
        components={{
          // eslint-disable-next-line react/no-multi-comp
          OpenPickerIcon: () =>
            options?.Icon && value ? (
              <>{options?.Icon}</>
            ) : (
              <ChevronRight
                style={{ transform: open ? 'rotateZ(90deg)' : 'rotateZ(0deg)', color: PLAIN_GREY }}
              />
            ),
        }}
      />
    </LocalizationProvider>
  );
};
